/**
 * ProfileCompletenessBanner · iter151 (2026-02-24)
 *
 * Persistenter Hinweis-Banner der oben auf allen Seiten erscheint, solange der
 * eingeloggte User keinen Avatar UND kein Coverbild (banner_url) hat. Verschwindet
 * automatisch sobald beide Bilder gesetzt sind. Nicht wegklickbar — Ziel ist
 * dass Creator ihr Profil vollständigen, damit sie in `/home/creators` sichtbar
 * werden (Public-Gate).
 *
 * Wird in AppShell.js gemountet, nur auf Nicht-Auth-Seiten sichtbar.
 */
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertCircle, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

// Diese Pfade sind Auth-/System-Seiten wo der Banner NICHT erscheinen soll
const HIDDEN_PATHS = ["/login", "/register", "/forgot-password", "/reset-password", "/verify-email", "/age-check"];

export default function ProfileCompletenessBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  // iter152.2-fix: Live-Zustand aus /api/profile ziehen — der useAuth-User ist
  // stale nach Profil-Updates. Refetch bei jedem Route-Wechsel damit der Banner
  // sofort verschwindet wenn beide Bilder gesetzt wurden.
  const [profile, setProfile] = useState(null);
  const [profileLoaded, setProfileLoaded] = useState(false);

  useEffect(() => {
    setCollapsed(false);
  }, [user?._id]);

  useEffect(() => {
    if (!user || user === false) { setProfile(null); setProfileLoaded(true); return; }
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/profile");
        if (!cancelled) setProfile(data || null);
      } catch { /* silent */ }
      finally { if (!cancelled) setProfileLoaded(true); }
    };
    load();
    // iter326 · Nach Avatar/Cover-Schnellbearbeitung sofort neu laden.
    const onUpdated = () => load();
    window.addEventListener("profile-updated", onUpdated);
    return () => { cancelled = true; window.removeEventListener("profile-updated", onUpdated); };
  }, [user?._id, location.pathname]);

  if (!user || user === false) return null;
  if (!profileLoaded) return null; // während erstem Laden nichts zeigen
  if (HIDDEN_PATHS.some((p) => location.pathname.startsWith(p))) return null;
  if (location.pathname.startsWith("/profile/edit")) return null;

  // iter152.2: prüfen an live-daten (profile > user)
  const avatarUrl = profile?.avatar_url || user?.avatar_url || "";
  const bannerUrl = profile?.banner_url || user?.banner_url || "";
  const missingAvatar = !avatarUrl;
  const missingBanner = !bannerUrl;
  if (!missingAvatar && !missingBanner) return null;

  const missing = [];
  if (missingAvatar) missing.push("Avatar");
  if (missingBanner) missing.push("Profilbild (Cover)");

  if (collapsed) {
    return (
      <button
        type="button"
        data-testid="profile-completeness-banner-collapsed"
        onClick={() => setCollapsed(false)}
        className="fixed bottom-4 right-4 z-40 w-11 h-11 rounded-full bg-amber-500 text-white shadow-lg flex items-center justify-center hover:bg-amber-600"
        aria-label={t("profile.completeness_open_aria")}
        title={t("profile.complete_title")}
      >
        <AlertCircle size={18} />
      </button>
    );
  }

  return (
    <div
      data-testid="profile-completeness-banner"
      className="bg-amber-50 border-b border-amber-200 text-amber-900"
    >
      <div className="max-w-[1280px] mx-auto px-4 py-2.5 flex items-center gap-3">
        <AlertCircle size={16} className="shrink-0" />
        <div className="flex-1 text-xs">
          <b>{t("profile.complete_prefix")}</b> Bitte lade noch {missing.join(" und ")} hoch, damit dein Profil öffentlich sichtbar wird.
        </div>
        <button
          type="button"
          data-testid="profile-completeness-cta"
          onClick={() => navigate("/profile/edit")}
          className="h-8 px-3 rounded-full bg-amber-600 text-white text-[11px] font-bold uppercase tracking-wider hover:bg-amber-700 shrink-0"
        >
          Jetzt hochladen
        </button>
        <button
          type="button"
          data-testid="profile-completeness-collapse"
          onClick={() => setCollapsed(true)}
          className="w-8 h-8 rounded-full flex items-center justify-center text-amber-700 hover:bg-amber-100 shrink-0"
          aria-label="Banner minimieren"
          title={t("profile.remind_later_title")}
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
